import { useEffect, useState } from 'react'
import { regionsApi, regionsAdminApi } from '@carrent/api'
import { Icon } from '@carrent/ui'

/**
 * Перевод машины в другой регион.
 *
 * Список регионов кроме текущего, причина перевода и история перемещений.
 * Выбор одного из списка — это радиогруппа, а не набор кнопок:
 * прежняя разметка не сообщала вспомогательным технологиям,
 * что вариант выбран.
 */
export function TransferCarModal({ car, onClose, onTransferred }) {
  const [regions, setRegions] = useState([])
  const [history, setHistory] = useState([])
  const [selRegion, setSelRegion] = useState(null)
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    let alive = true
    Promise.all([regionsApi.list(), regionsAdminApi.carHistory(car.id)])
      .then(([r1, r2]) => {
        if (!alive) return
        setRegions(r1.data?.data || [])
        setHistory(r2.data?.data?.history || [])
      })
      .catch(() => alive && setLoadError('Не удалось загрузить регионы и историю'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [car.id])

  // Закрытие по Escape — модалка без этого не соответствует ожиданиям.
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const submit = async () => {
    if (!selRegion) {
      setErr('Выберите регион')
      return
    }
    setErr('')
    setSaving(true)
    try {
      await regionsAdminApi.transferCar(car.id, selRegion, reason)
      onTransferred()
    } catch (e) {
      setErr(e.response?.data?.message || 'Ошибка перевода')
    } finally {
      setSaving(false)
    }
  }

  const otherRegions = regions.filter((r) => r.id !== car.region_id)

  return (
    <div
      className="sheet-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Перевод машины в другой регион"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="sheet">
        <div className="sheet__header">
          <h3 className="sheet__title">Перевести в регион</h3>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="card card--flat transfer-current">
          <p className="transfer-current__label">Сейчас в регионе:</p>
          <p className="transfer-current__value">
            {car.region ? `${car.region.name} (${car.region.code})` : '— Не задан —'}
          </p>
        </div>

        {loading && (
          <div className="state">
            <span className="spinner" />
          </div>
        )}

        {!loading && loadError && <div className="alert alert--error">{loadError}</div>}

        {!loading && !loadError && (
          <>
            <p className="section-title">Перевести в:</p>

            {otherRegions.length === 0 ? (
              <div className="state">
                <p className="state__title">Нет других регионов</p>
                <p className="state__text">Создайте ещё один регион, чтобы переводить машины.</p>
              </div>
            ) : (
              <div className="region-list" role="radiogroup" aria-label="Регион назначения">
                {otherRegions.map((r) => {
                  const active = selRegion === r.id
                  return (
                    <label key={r.id} className={`region-option ${active ? 'is-active' : ''}`}>
                      <input
                        type="radio"
                        name="transfer-region"
                        className="visually-hidden"
                        checked={active}
                        onChange={() => setSelRegion(r.id)}
                      />
                      <span className="region-option__code">{r.code}</span>
                      <span className="region-option__name">{r.name}</span>
                    </label>
                  )
                })}
              </div>
            )}

            <div className="field mt">
              <label className="field__label" htmlFor="transfer-reason">
                Причина перевода (опционально)
              </label>
              <textarea
                id="transfer-reason"
                className="input input--textarea"
                rows={2}
                placeholder="Напр.: переезд на другой склад, передача водителю в другом городе…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            <div className="alert alert--warning mt">
              <Icon name="warning" size={14} />
              После перевода машина не будет видна водителям и менеджерам прежнего региона.
              Текущая поездка, если она идёт, продолжится — водитель сможет её закончить.
            </div>

            {err && <div className="alert alert--error mt">{err}</div>}

            <button
              type="button"
              className="btn btn--primary btn--full mt"
              disabled={!selRegion || saving}
              onClick={submit}
            >
              {saving ? <span className="spinner spinner--sm" /> : <Icon name="car" size={16} />}
              {saving ? 'Переводим…' : 'Перевести машину'}
            </button>

            {history.length > 0 && (
              <>
                <p className="section-title">История перемещений</p>
                <div className="transfer-history">
                  {history.map((h) => (
                    <div key={h.id} className="transfer-history__row">
                      <p className="transfer-history__route">
                        <span className="transfer-history__from">{h.from_region?.code || '—'}</span>
                        <Icon name="chevronRight" size={12} />
                        <span className="transfer-history__to">{h.to_region?.code}</span>
                      </p>
                      <p className="transfer-history__meta">
                        {new Date(h.created_at).toLocaleString('ru', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                        {h.actor && <> · {h.actor.full_name}</>}
                      </p>
                      {h.reason && <p className="transfer-history__reason">«{h.reason}»</p>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
